import React from 'react';
import { alpha, useTheme } from '@mui/material/styles';
import { Box, Tooltip, Typography } from '@mui/material';
import { WorkspacePremiumOutlined } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useTranslation } from '../../../hooks/useTranslation';
import useAuth from '../../../hooks/useAuth';
import { canPerformAction, PERMISSIONS } from '../../../utils/permissions';

export type NavBarPlanBadgeProps = {
  label: string | null;
  /** Tighter max width (mobile nav) */
  compact?: boolean;
  /** Softer look when nested inside the white top-bar pill */
  embeddedInTopBar?: boolean;
};

/**
 * Pill-style current plan for top nav / desktop usage strip.
 */
const NavBarPlanBadge: React.FC<NavBarPlanBadgeProps> = ({
  label,
  compact = false,
  embeddedInTopBar = false,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const canLinkToBilling =
    !!currentUser && canPerformAction(currentUser, PERMISSIONS.MANAGE_BILLING);

  if (!label) return null;

  const maxW = compact ? { xs: 160, sm: 280 } : { xs: 200, sm: 360, md: 420 };

  return (
    <Tooltip title={label} placement="bottom" enterDelay={400}>
      <Box
        {...(canLinkToBilling
          ? { component: Link, to: '/billing', 'aria-label': t('Current plan') }
          : { component: 'span' })}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.875,
          px: embeddedInTopBar ? 0.5 : 1.35,
          py: embeddedInTopBar ? 0.25 : 0.55,
          maxWidth: maxW,
          minWidth: 0,
          borderRadius: embeddedInTopBar ? 1.5 : 999,
          textDecoration: 'none',
          color: 'text.primary',
          border: embeddedInTopBar ? 0 : 1,
          borderColor: embeddedInTopBar
            ? 'transparent'
            : alpha(theme.palette.primary.main, 0.22),
          bgcolor: embeddedInTopBar
            ? 'transparent'
            : alpha(theme.palette.primary.main, 0.07),
          boxShadow: embeddedInTopBar
            ? 'none'
            : `0 1px 0 ${alpha(theme.palette.common.black, 0.04)}`,
          transition: theme.transitions.create(
            ['border-color', 'background-color', 'box-shadow', 'transform'],
            { duration: theme.transitions.duration.shortest }
          ),
          '&:hover': {
            borderColor: embeddedInTopBar
              ? 'transparent'
              : alpha(theme.palette.primary.main, 0.45),
            bgcolor: embeddedInTopBar
              ? alpha(theme.palette.primary.main, 0.06)
              : alpha(theme.palette.primary.main, 0.11),
            boxShadow: embeddedInTopBar
              ? 'none'
              : `0 2px 8px ${alpha(theme.palette.primary.main, 0.12)}`,
          },
          '&:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <WorkspacePremiumOutlined
          sx={{
            fontSize: '1.05rem',
            color: 'primary.main',
            flexShrink: 0,
            opacity: 0.92,
          }}
          aria-hidden
        />
        <Typography
          component="span"
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: compact ? '0.72rem' : '0.75rem',
            letterSpacing: 0.02,
            lineHeight: 1.35,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.primary',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default NavBarPlanBadge;
